import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ContentEditable from 'react-contenteditable';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Input,
  Label,
  Spinner,
  Alert,
} from 'reactstrap';

// Lightweight, block-based email template builder (no DB schema changes)
// Stores layout JSON inside the saved HTML as an HTML comment for round-trip.

const BUILDER_MARKER_START = '<!--__EMAIL_BUILDER_JSON__:';
const BUILDER_MARKER_END = '-->';

const blockLibrary = {
  header: () => ({
    id: uuidv4(),
    type: 'header',
    logo: 'https://unternehmen.hello-doctor.de/_next/image?url=%2Fimages%2Fhello-doctor-logo.png&w=384&q=75',
    align: 'center',
    bg: '#ffffff',
  }),
  title: () => ({ id: uuidv4(), type: 'title', text: 'Titel eingeben', color: '#464646' }),
  paragraph: () => ({ id: uuidv4(), type: 'paragraph', html: 'Absatztext…', color: '#555555', align: 'left' }),
  richtext: () => ({ id: uuidv4(), type: 'richtext', html: '<p>Freier Text …</p>', color: '#555555', align: 'left' }),
  image: () => ({ id: uuidv4(), type: 'image', src: 'https://via.placeholder.com/600x240', width: '100%' }),
  button: () => ({ id: uuidv4(), type: 'button', text: 'Jetzt öffnen', url: '#', bg: '#44aefe', color: '#ffffff' }),
  divider: () => ({ id: uuidv4(), type: 'divider', color: '#e9ecef', height: 2 }),
  footer: () => ({ id: uuidv4(), type: 'footer', bg: '#000d29', color: '#ffffff', align: 'center', html: '' }),
  footer_modular: () => ({
    id: uuidv4(),
    type: 'footer_modular',
    bg: '#000d29',
    color: '#ffffff',
    align: 'center',
    sections: [
      {
        id: uuidv4(),
        type: 'social',
        html:
          '<a href="https://www.facebook.com/hellodoctor.de" target="_blank" rel="noopener noreferrer" style="margin-right:10px;text-decoration:none"><img src="https://hello-doctor.de/api/HTML/fb.png" alt="Facebook" width="32" /></a>'+
          '<a href="https://www.instagram.com/hellodoctor_de/" target="_blank" rel="noopener noreferrer" style="margin-left:10px;text-decoration:none"><img src="https://hello-doctor.de/api/HTML/insta.png" alt="Instagram" width="32" /></a>'+
          '<a href="https://www.tiktok.com/@hellodoctor_de" target="_blank" rel="noopener noreferrer" style="margin-left:10px;text-decoration:none"><img src="https://cdn-icons-png.flaticon.com/512/3046/3046121.png" alt="TikTok" width="30" /></a>'+
          '<a href="https://www.linkedin.com/company/hellodoctor-de" target="_blank" rel="noopener noreferrer" style="margin-left:10px;text-decoration:none"><img src="https://img.icons8.com/ios-filled/50/ffffff/linkedin-circled--v1.png" alt="LinkedIn" width="32" /></a>'
      },
      {
        id: uuidv4(),
        type: 'stores',
        html:
          '<a href="https://play.google.com/store/apps/details?id=com.guelgmbh.hellodoctor" target="_blank" rel="noopener noreferrer" style="margin-right:15px;text-decoration:none"><img src="https://hello-doctor.de/img2/Google_play_logo.png" alt="Google Play" width="120"/></a>'+
          '<a href="https://apps.apple.com/app/id6444258989" target="_blank" rel="noopener noreferrer" style="text-decoration:none"><img src="https://hello-doctor.de/img2/Apple_store_logo.png" alt="Apple Store" width="120"/></a>'
      },
      {
        id: uuidv4(),
        type: 'legal',
        html:
          '<a href="https://hello-doctor.de/Impressum" style="color:inherit;margin-right:12px;text-decoration:none">Impressum</a>'+
          '<a href="https://hello-doctor.de/Kontakt" style="color:inherit;margin-right:12px;text-decoration:none">Kontakt</a>'+
          '<a href="https://hello-doctor.de/AGB" style="color:inherit;margin-right:12px;text-decoration:none">AGB</a>'+
          '<a href="https://hello-doctor.de/Datenschutz" style="color:inherit;text-decoration:none">Datenschutz</a>'
      }
    ]
  }),
};

const initialLayout = [blockLibrary.header(), blockLibrary.title(), blockLibrary.paragraph(), blockLibrary.button(), blockLibrary.footer()];

const AUDIENCE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'candidate', label: 'Candidate' },
  { value: 'hospital', label: 'Hospital' },
];

const SimpleEmailBuilder = () => {
  const { email_id } = useParams();
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [audience, setAudience] = useState('admin');
  const [blocks, setBlocks] = useState(initialLayout);
  const [selectedId, setSelectedId] = useState(blocks[0].id);
  const [loading, setLoading] = useState(!!email_id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const selected = useMemo(() => blocks.find(b => b.id === selectedId) || null, [blocks, selectedId]);

  const addBlock = (type) => {
    const b = blockLibrary[type]();
    setBlocks(prev => [...prev, b]);
    setSelectedId(b.id);
  };

  const removeBlock = (id) => {
    setBlocks(prev => prev.filter(b => b.id !== id));
    if (selectedId === id && blocks.length > 1) setSelectedId(blocks[0]?.id);
  };

  const updateBlock = (id, patch) => {
    setBlocks(prev => prev.map(b => (b.id === id ? { ...b, ...patch } : b)));
  };

  // Basic HTML sanitizer to keep formatting but strip unsafe tags/attrs
  const sanitizeHtml = (html) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
      const allowed = new Set(['B','STRONG','I','EM','U','BR','P','SPAN','A','UL','OL','LI','H1','H2','H3','IMG']);
      const walk = (node) => {
        [...node.children].forEach(child => {
          if (!allowed.has(child.tagName)) {
            const parent = child.parentNode;
            while (child.firstChild) parent.insertBefore(child.firstChild, child);
            parent.removeChild(child);
          } else {
            [...child.attributes].forEach(attr => {
              const name = attr.name.toLowerCase();
              if (child.tagName === 'A' && name === 'href') {
                const val = child.getAttribute('href') || '';
                if (!/^https?:\/\//.test(val) && !/^mailto:/.test(val)) child.removeAttribute('href');
                child.setAttribute('target','_blank');
                child.setAttribute('rel','noopener noreferrer');
              } else if (child.tagName === 'IMG' && ['src','alt','width','height'].includes(name)) {
                // allow
              } else if (name === 'style') {
                const safe = (child.getAttribute('style')||'').replace(/position|left|top|behavior|expression/gi,'');
                child.setAttribute('style', safe);
              } else if (/^on/i.test(name)) {
                child.removeAttribute(attr.name);
              } else if (!['href','style','target','rel','src','alt','width','height'].includes(name)) {
                child.removeAttribute(attr.name);
              }
            });
            walk(child);
          }
        });
      };
      walk(doc.body.firstElementChild);
      return doc.body.firstElementChild.innerHTML;
    } catch {
      return html;
    }
  };

  const [activeEditableId, setActiveEditableId] = useState(null);
  const exec = (cmd, val = null) => {
    try { document.execCommand(cmd, false, val); } catch {}
  };
  const insertMergeTag = (tag) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(tag));
    range.setStart(range.endContainer, range.endOffset);
    range.collapse(true);
    sel.removeAllRanges(); sel.addRange(range);
  };
  const promptLink = () => {
    const url = window.prompt('Link URL eingeben (https://...)');
    if (!url) return;
    exec('createLink', url);
  };
  const promptImage = () => {
    const url = window.prompt('Bild URL eingeben (https://...)');
    if (!url) return;
    exec('insertImage', url);
  };

  const getSampleFooterHtml = () => `
    <div style="padding:12px 0">
      <div style="text-align:center;margin-bottom:14px">
        <a href="https://www.facebook.com/hellodoctor.de" target="_blank" rel="noopener noreferrer" style="margin-right:10px;text-decoration:none">
          <img src="https://hello-doctor.de/api/HTML/fb.png" alt="Facebook" width="40" />
        </a>
        <a href="https://www.instagram.com/hellodoctor_de/" target="_blank" rel="noopener noreferrer" style="margin-left:10px;text-decoration:none">
          <img src="https://hello-doctor.de/api/HTML/insta.png" alt="Instagram" width="40" />
        </a>
        <a href="https://www.tiktok.com/@hellodoctor_de" target="_blank" rel="noopener noreferrer" style="margin-left:10px;text-decoration:none">
          <img src="https://cdn-icons-png.flaticon.com/512/3046/3046121.png" alt="TikTok" width="34" />
        </a>
        <a href="https://www.linkedin.com/company/hellodoctor-de" target="_blank" rel="noopener noreferrer" style="margin-left:10px;text-decoration:none">
          <img src="https://img.icons8.com/ios-filled/50/ffffff/linkedin-circled--v1.png" alt="LinkedIn" width="40" />
        </a>
      </div>
      <div style="text-align:center;margin:10px 0;font-weight:bold">Mit der Hello-Doctor App stets auf dem neuesten Stand bei Ärztestellen</div>
      <div style="text-align:center;margin:10px 0">
        <a href="https://play.google.com/store/apps/details?id=com.guelgmbh.hellodoctor" target="_blank" rel="noopener noreferrer" style="margin-right:15px;text-decoration:none">
          <img src="https://hello-doctor.de/img2/Google_play_logo.png" alt="Google Play" width="140" />
        </a>
        <a href="https://apps.apple.com/app/id6444258989" target="_blank" rel="noopener noreferrer" style="text-decoration:none">
          <img src="https://hello-doctor.de/img2/Apple_store_logo.png" alt="Apple Store" width="140" />
        </a>
      </div>
      <div style="text-align:center;margin:10px 0">
        <a href="https://apps.apple.com/app/id6444258989" target="_blank" rel="noopener noreferrer" style="text-decoration:none">
          <img src="https://hello-doctor.de/HTML/Hello-doctor.png" alt="QR" width="100" />
        </a>
      </div>
      <div style="text-align:center;margin:10px 0">
        <a href="https://hello-doctor.de/Impressum" style="color:inherit;margin-right:12px;text-decoration:none">Impressum</a>
        <a href="https://hello-doctor.de/Kontakt" style="color:inherit;margin-right:12px;text-decoration:none">Kontakt</a>
        <a href="https://hello-doctor.de/AGB" style="color:inherit;margin-right:12px;text-decoration:none">AGB</a>
        <a href="https://hello-doctor.de/Datenschutz" style="color:inherit;text-decoration:none">Datenschutz</a>
      </div>
      <div style="text-align:center;margin:12px 0;font-size:13px">
        Gül GmbH · Geschäftsführer: Deniz Gül · HRB 100749
      </div>
      <div style="text-align:center;margin:10px 0">
        Für weitere Fragen und Anregungen erreichen Sie uns über <strong>support@hello-doctor.de</strong>
      </div>
      <div style="text-align:center;margin:10px 0">
        <a href="https://hello-doctor.de" style="text-decoration:none;color:inherit">
          <img src="https://hello-doctor.de/api/img2/logo1.png" alt="Hello Doctor" width="100" />
        </a>
      </div>
      <div style="text-align:center;margin:10px 0">&copy; ${new Date().getFullYear()} Hello-Doctor.de Alle Rechte vorbehalten</div>
      <div style="text-align:center;margin:10px 0">Diese E-Mail wurde von <a href="https://hello-doctor.de" style="color:#77a6f2">Hello-Doctor.de</a> gesendet. Hier können Sie sich <a href="{{link_unsubscribe}}" style="color:#77a6f2">abmelden</a>.</div>
    </div>
  `;

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const arr = Array.from(blocks);
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    setBlocks(arr);
  };

  const extractEmbeddedJson = (html) => {
    const start = html.indexOf(BUILDER_MARKER_START);
    if (start === -1) return null;
    const end = html.indexOf(BUILDER_MARKER_END, start + BUILDER_MARKER_START.length);
    if (end === -1) return null;
    const jsonStr = html.substring(start + BUILDER_MARKER_START.length, end);
    try { return JSON.parse(jsonStr); } catch { return null; }
  };

  const wrapEmail = (inner) => `<!DOCTYPE html><html><head><meta http-equiv="Content-Type" content="text/html; charset=utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1.0" /><title>${subject || ''}</title></head><body style="background:#e1e1e1;font-family:Arial,Helvetica,sans-serif;font-size:1em;"><center style="width:100%;background:#e1e1e1;"><div style="max-width:640px;margin:auto;" class="email-container">${inner}</div></center></body></html>`;

  const row = (inner, style = '') => `<table width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:640px;${style}"><tr><td>${inner}</td></tr></table>`;

  const renderBlocksToHtml = () => {
    const parts = blocks.map(b => {
      switch (b.type) {
        case 'header':
          return row(`<table width=\"100%\" cellspacing=\"0\" cellpadding=\"0\" border=\"0\" style=\"background:${b.bg}\"><tr><td align=\"${b.align}\" style=\"padding:10px 30px\"><img src=\"${b.logo}\" alt=\"Logo\" style=\"max-width:180px;height:auto\"/></td></tr></table>`);
        case 'title':
          return row(`<div style=\"padding:24px 30px;text-align:left\"><h2 style=\"margin:0;color:${b.color}\">${b.text}</h2></div>`, 'background:#fff;');
        case 'paragraph':
          return row(`<div style=\"padding:0 30px 16px 30px;color:${b.color};text-align:${b.align}\">${b.html}</div>`, 'background:#fff;');
        case 'richtext':
          return row(`<div style=\"padding:16px 30px;color:${b.color};text-align:${b.align}\">${b.html}</div>`, 'background:#fff;');
        case 'image':
          return row(`<div style=\"text-align:center;padding:16px\"><img src=\"${b.src}\" style=\"width:${b.width};height:auto\"/></div>`, 'background:#fff;');
        case 'button':
          return row(`<div style=\"text-align:center;padding:16px\"><a href=\"${b.url}\" style=\"display:inline-block;background:${b.bg};color:${b.color};padding:12px 20px;border-radius:6px;text-decoration:none\">${b.text}</a></div>`, 'background:#fff;');
        case 'divider':
          return row(`<div style=\"height:${b.height}px;background:${b.color};margin:20px 0\"></div>`, 'background:#fff;');
        case 'footer':
          return row(`<div style=\"padding:12px;color:${b.color};background:${b.bg};text-align:${b.align || 'center'}\">${b.html || ''}</div>`);
        case 'footer_modular': {
          const body = (b.sections || []).map(sec => `<div style=\"margin:8px 0\">${sec.html}</div>`).join('');
          return row(`<div style=\"padding:12px;color:${b.color};background:${b.bg};text-align:${b.align || 'center'}\">${body}</div>`);
        }
        default:
          return '';
      }
    });

    const jsonComment = `${BUILDER_MARKER_START}${JSON.stringify({ name, subject, blocks })}${BUILDER_MARKER_END}`;
    return wrapEmail(jsonComment + parts.join(''));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const html = renderBlocksToHtml();
      const design = { name, subject, blocks };
      const payload = {
        name: name || 'Unbenannte Vorlage',
        subject: subject || '(ohne Betreff)',
        audience,
        htmlContent: html,
        design_json: design,
      };
      if (email_id) {
        await axios.put(`/api2/email-templates/${email_id}`, payload);
      } else {
        await axios.post('/api2/email-templates', payload);
      }
      alert('Vorlage gespeichert');
    } catch (e) {
      console.error(e);
      setError('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const loadExisting = useCallback(async () => {
    if (!email_id) return;
    try {
      setLoading(true);
      const res = await axios.get(`/api2/email-templates/${email_id}`);
      const t = res.data || {};
      setName(t.name || '');
      setSubject(t.subject || '');
      setAudience((t.audience || 'admin').toLowerCase());
      let restored = null;
      if (t.design_json) {
        try { restored = typeof t.design_json === 'string' ? JSON.parse(t.design_json) : t.design_json; } catch (_) {}
      }
      if (!restored) {
        const embedded = extractEmbeddedJson(t.html_content || t.htmlContent || '');
        if (embedded) restored = embedded;
      }
      if (restored && Array.isArray(restored.blocks)) {
        setBlocks(restored.blocks);
        setSelectedId(restored.blocks[0]?.id || null);
      }
    } catch (e) {
      console.error(e);
      setError('Vorlage konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [email_id]);

  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const openPreview = () => {
    setPreviewHtml(renderBlocksToHtml());
    setShowPreview(true);
  };

  useEffect(() => { loadExisting(); }, [loadExisting]);

  return (
    <div className="content">
      <Card>
        <CardHeader className="d-flex align-items-center justify-content-between">
          <div style={{display:'flex',gap:12,alignItems:'flex-end',flexWrap:'wrap'}}>
            <div>
              <Label className="mb-0 small">Name</Label>
              <Input value={name} onChange={(e)=>setName(e.target.value)} placeholder="Vorlagenname" />
            </div>
            <div>
              <Label className="mb-0 small">Betreff</Label>
              <Input value={subject} onChange={(e)=>setSubject(e.target.value)} placeholder="E-Mail Betreff" />
            </div>
            <div>
              <Label className="mb-0 small">Empfänger</Label>
              <Input type="select" value={audience} onChange={(e)=>setAudience(e.target.value)}>
                {AUDIENCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </div>
          </div>
          <div>
            <Button color="primary" onClick={handleSave} disabled={saving || loading}>{saving ? <Spinner size="sm" /> : 'Speichern'}</Button>
          </div>
        </CardHeader>
        <CardBody>
          {error && <Alert color="danger">{error}</Alert>}
          <Row>
            <Col md={3}>
              <div className="mb-2 font-weight-bold">Bausteine</div>
              <div className="d-flex flex-column" style={{gap:8}}>
                <Button size="sm" color="light" onClick={()=>addBlock('header')}>Header</Button>
                <Button size="sm" color="light" onClick={()=>addBlock('title')}>Titel</Button>
                <Button size="sm" color="light" onClick={()=>addBlock('paragraph')}>Absatz</Button>
                <Button size="sm" color="light" onClick={()=>addBlock('richtext')}>Rich Text</Button>
                <Button size="sm" color="light" onClick={()=>addBlock('image')}>Bild</Button>
                <Button size="sm" color="light" onClick={()=>addBlock('button')}>Button</Button>
                <Button size="sm" color="light" onClick={()=>addBlock('divider')}>Trenner</Button>
                <Button size="sm" color="light" onClick={()=>addBlock('footer')}>Footer (einfach)</Button>
                <Button size="sm" color="light" onClick={()=>addBlock('footer_modular')}>Footer (modular)</Button>
              </div>
              <hr/>
              {selected && (
                <div>
                  <div className="mb-2 font-weight-bold">Eigenschaften</div>
                  {selected.type === 'header' && (
                    <>
                      <Label>Logo URL</Label>
                      <Input value={selected.logo} onChange={(e)=>updateBlock(selected.id,{logo:e.target.value})} />
                      <Label className="mt-2">Ausrichtung</Label>
                      <Input type="select" value={selected.align} onChange={(e)=>updateBlock(selected.id,{align:e.target.value})}>
                        <option>left</option>
                        <option>center</option>
                        <option>right</option>
                      </Input>
                    </>
                  )}
                  {selected.type === 'title' && (
                    <>
                      <Label>Farbe</Label>
                      <Input type="color" value={selected.color} onChange={(e)=>updateBlock(selected.id,{color:e.target.value})} />
                    </>
                  )}
                  {selected.type === 'paragraph' && (
                    <>
                      <Label>Farbe</Label>
                      <Input type="color" value={selected.color} onChange={(e)=>updateBlock(selected.id,{color:e.target.value})} />
                      <Label className="mt-2">Ausrichtung</Label>
                      <Input type="select" value={selected.align} onChange={(e)=>updateBlock(selected.id,{align:e.target.value})}>
                        <option>left</option>
                        <option>center</option>
                        <option>right</option>
                      </Input>
                    </>
                  )}
                  {selected.type === 'richtext' && (
                    <>
                      <Label>Farbe</Label>
                      <Input type="color" value={selected.color} onChange={(e)=>updateBlock(selected.id,{color:e.target.value})} />
                      <Label className="mt-2">Ausrichtung</Label>
                      <Input type="select" value={selected.align} onChange={(e)=>updateBlock(selected.id,{align:e.target.value})}>
                        <option>left</option>
                        <option>center</option>
                        <option>right</option>
                      </Input>
                    </>
                  )}
                  {selected.type === 'image' && (
                    <>
                      <Label>Bild URL</Label>
                      <Input value={selected.src} onChange={(e)=>updateBlock(selected.id,{src:e.target.value})} />
                      <Label className="mt-2">Breite</Label>
                      <Input value={selected.width} onChange={(e)=>updateBlock(selected.id,{width:e.target.value})} />
                    </>
                  )}
                  {selected.type === 'button' && (
                    <>
                      <Label>Text</Label>
                      <Input value={selected.text} onChange={(e)=>updateBlock(selected.id,{text:e.target.value})} />
                      <Label className="mt-2">Link</Label>
                      <Input value={selected.url} onChange={(e)=>updateBlock(selected.id,{url:e.target.value})} />
                      <Label className="mt-2">Hintergrund</Label>
                      <Input type="color" value={selected.bg} onChange={(e)=>updateBlock(selected.id,{bg:e.target.value})} />
                      <Label className="mt-2">Textfarbe</Label>
                      <Input type="color" value={selected.color} onChange={(e)=>updateBlock(selected.id,{color:e.target.value})} />
                    </>
                  )}
                  {selected.type === 'divider' && (
                    <>
                      <Label>Farbe</Label>
                      <Input type="color" value={selected.color} onChange={(e)=>updateBlock(selected.id,{color:e.target.value})} />
                      <Label className="mt-2">Höhe</Label>
                      <Input type="number" value={selected.height} onChange={(e)=>updateBlock(selected.id,{height:e.target.value})} />
                    </>
                  )}
                  {selected.type === 'footer' && (
                    <>
                      <Label>Hintergrund</Label>
                      <Input type="color" value={selected.bg} onChange={(e)=>updateBlock(selected.id,{bg:e.target.value})} />
                      <Label className="mt-2">Textfarbe</Label>
                      <Input type="color" value={selected.color} onChange={(e)=>updateBlock(selected.id,{color:e.target.value})} />
                      <Label className="mt-2">Ausrichtung</Label>
                      <Input type="select" value={selected.align} onChange={(e)=>updateBlock(selected.id,{align:e.target.value})}>
                        <option>left</option>
                        <option>center</option>
                        <option>right</option>
                      </Input>
                      <div className="mt-2">Inhalt</div>
                      <div className="mb-2" style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                        <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('bold')}>B</Button>
                        <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('italic')}>I</Button>
                        <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('underline')}>U</Button>
                        <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('insertUnorderedList')}>• Liste</Button>
                        <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('insertOrderedList')}>1. Liste</Button>
                        <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>promptLink()}>Link</Button>
                        <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>promptImage()}>Bild</Button>
                        <Button size="sm" color="secondary" onMouseDown={(e)=>e.preventDefault()} onClick={()=>updateBlock(selected.id,{html: sanitizeHtml(getSampleFooterHtml())})}>Sample laden</Button>
                      </div>
                      <ContentEditable
                        html={selected.html}
                        onChange={(e)=>updateBlock(selected.id,{html:sanitizeHtml(e.target.value)})}
                      />
                    </>
                  )}
                  {selected.type === 'footer_modular' && (
                    <>
                      <Label>Hintergrund</Label>
                      <Input type="color" value={selected.bg} onChange={(e)=>updateBlock(selected.id,{bg:e.target.value})} />
                      <Label className="mt-2">Textfarbe</Label>
                      <Input type="color" value={selected.color} onChange={(e)=>updateBlock(selected.id,{color:e.target.value})} />
                      <Label className="mt-2">Ausrichtung</Label>
                      <Input type="select" value={selected.align || 'center'} onChange={(e)=>updateBlock(selected.id,{align:e.target.value})}>
                        <option>left</option>
                        <option>center</option>
                        <option>right</option>
                      </Input>
                      <hr/>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="font-weight-bold">Sektionen</div>
                        <div className="d-flex" style={{gap:6}}>
                          <Button size="sm" onClick={()=>updateBlock(selected.id,{sections:[...(selected.sections||[]),{id:uuidv4(),type:'html',html:'<p>Neuer Abschnitt</p>'}]})}>+ Text</Button>
                          <Button size="sm" onClick={()=>updateBlock(selected.id,{sections:[...(selected.sections||[]),{id:uuidv4(),type:'social',html:blockLibrary.footer_modular().sections[0].html}]})}>+ Social</Button>
                          <Button size="sm" onClick={()=>updateBlock(selected.id,{sections:[...(selected.sections||[]),{id:uuidv4(),type:'stores',html:blockLibrary.footer_modular().sections[1].html}]})}>+ Stores</Button>
                          <Button size="sm" onClick={()=>updateBlock(selected.id,{sections:[...(selected.sections||[]),{id:uuidv4(),type:'legal',html:blockLibrary.footer_modular().sections[2].html}]})}>+ Legal</Button>
                        </div>
                      </div>
                      {(selected.sections||[]).map((sec, idx)=> (
                        <div key={sec.id} style={{border:'1px solid #e5e7eb',borderRadius:6,padding:8,marginBottom:8}}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="small text-muted">{sec.type?.toUpperCase() || 'HTML'} (#{idx+1})</div>
                            <div style={{display:'flex',gap:6}}>
                              <Button size="sm" disabled={idx===0} onClick={()=>{
                                const arr=[...(selected.sections||[])]; const [m]=arr.splice(idx,1); arr.splice(idx-1,0,m); updateBlock(selected.id,{sections:arr});
                              }}>↑</Button>
                              <Button size="sm" disabled={idx===(selected.sections.length-1)} onClick={()=>{
                                const arr=[...(selected.sections||[])]; const [m]=arr.splice(idx,1); arr.splice(idx+1,0,m); updateBlock(selected.id,{sections:arr});
                              }}>↓</Button>
                              <Button size="sm" color="link" onClick={()=>{
                                const arr=[...(selected.sections||[])]; arr.splice(idx,1); updateBlock(selected.id,{sections:arr});
                              }}>Entfernen</Button>
                            </div>
                          </div>
                          <div className="mb-2" style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                            <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('bold')}>B</Button>
                            <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('italic')}>I</Button>
                            <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('underline')}>U</Button>
                            <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('insertUnorderedList')}>• Liste</Button>
                            <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('insertOrderedList')}>1. Liste</Button>
                            <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>promptLink()}>Link</Button>
                            <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>promptImage()}>Bild</Button>
                          </div>
                          <ContentEditable html={sec.html} onChange={(e)=>{
                            const arr=[...(selected.sections||[])];
                            arr[idx] = { ...sec, html: sanitizeHtml(e.target.value) };
                            updateBlock(selected.id,{sections:arr});
                          }} />
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </Col>
            <Col md={9}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <Button color="light" size="sm" onClick={openPreview}>Vorschau</Button>
                  <Button color="light" size="sm" className="ml-2" onClick={()=>setShowPreview(!showPreview)}>
                    {showPreview ? 'Editor ausblenden' : 'Live Vorschau'}
                  </Button>
                </div>
                <div className="text-right">
                  <small className="text-muted d-block">Drag & Drop zum Umordnen</small>
                  <small className="text-muted">Platzhalter: {'{{placeholders}}'}</small>
                </div>
              </div>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {blocks.map((b, index) => (
                        <Draggable key={b.id} draggableId={b.id} index={index}>
                          {(prov) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              onClick={()=>setSelectedId(b.id)}
                              style={{ border: selectedId===b.id? '2px solid #0d6efd':'1px solid #e5e7eb', borderRadius:6, padding:12, marginBottom:8, background:'#fff', cursor:'move', ...prov.draggableProps.style }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="small text-muted">{b.type.toUpperCase()}</div>
                                <Button size="sm" color="link" onClick={(e)=>{e.stopPropagation(); removeBlock(b.id);}}>Entfernen</Button>
                              </div>
                              {(b.type === 'title') && (
                                <div onFocus={()=>setActiveEditableId(b.id)} onBlur={()=>setActiveEditableId(null)}>
                                  {activeEditableId===b.id && (
                                    <div className="mb-2" style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('bold')}>B</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('italic')}>I</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('underline')}>U</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>promptLink()}>Link</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>promptImage()}>Bild</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>insertMergeTag('{{f_name}}')}>+ Vorname</Button>
                                    </div>
                                  )}
                                  <ContentEditable html={b.text}
                                    onChange={(e)=>updateBlock(b.id,{text:sanitizeHtml(e.target.value)})}
                                  />
                                </div>
                              )}
                              {(b.type === 'paragraph' || b.type === 'richtext') && (
                                <div onFocus={()=>setActiveEditableId(b.id)} onBlur={()=>setActiveEditableId(null)}>
                                  {activeEditableId===b.id && (
                                    <div className="mb-2" style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('bold')}>B</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('italic')}>I</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('underline')}>U</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('insertUnorderedList')}>• Liste</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('insertOrderedList')}>1. Liste</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>promptLink()}>Link</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>promptImage()}>Bild</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>insertMergeTag('{{{message}}}')}>+ Nachricht</Button>
                                    </div>
                                  )}
                                  <ContentEditable html={b.html}
                                    onChange={(e)=>updateBlock(b.id,{html:sanitizeHtml(e.target.value)})}
                                  />
                                </div>
                              )}
                              {b.type === 'image' && (
                                <img alt="Bild" src={b.src} style={{maxWidth:'100%', height:'auto'}} />
                              )}
                              {b.type === 'button' && (
                                <Button style={{background:b.bg, color:b.color, border:'none'}}>{b.text}</Button>
                              )}
                              {b.type === 'divider' && (
                                <div style={{height:b.height, background:b.color}} />
                              )}
                              {b.type === 'header' && (
                                <img alt="Logo" src={b.logo} style={{maxWidth:180, height:'auto'}} />
                              )}
                              {b.type === 'footer' && (
                                <div onFocus={()=>setActiveEditableId(b.id)} onBlur={()=>setActiveEditableId(null)}>
                                  {activeEditableId===b.id && (
                                    <div className="mb-2" style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('bold')}>B</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('italic')}>I</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('underline')}>U</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('insertUnorderedList')}>• Liste</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>exec('insertOrderedList')}>1. Liste</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>promptLink()}>Link</Button>
                                      <Button size="sm" onMouseDown={(e)=>e.preventDefault()} onClick={()=>promptImage()}>Bild</Button>
                                    </div>
                                  )}
                                  <div style={{background:b.bg, color:b.color, padding:12, textAlign:b.align}}>
                                    <ContentEditable
                                      html={b.html}
                                      onChange={(e)=>updateBlock(b.id,{html:sanitizeHtml(e.target.value)})}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              {showPreview && (
                <div className="mt-4">
                  <h6>Live Vorschau</h6>
                  <iframe title="preview" style={{width:'100%',height:'480px',borderRadius:'12px',border:'1px solid #e5e7eb'}} srcDoc={previewHtml} />
                </div>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  );
};

export default SimpleEmailBuilder;
